import React, { useEffect, useMemo, useState } from "react";
import Chart, {
  Series,
  ArgumentAxis,
  ValueAxis,
  Legend,
  Tooltip,
  Grid,
  Label,
} from "devextreme-react/chart";
import "devextreme/dist/css/dx.light.css";

interface BudgetChartProps {
  cobDate?: Date | null;
  elementName?: string;
  apiBase?: string;
}

interface ChartRow {
  date: Date;
  dateText: string;
  pnl_fytd: number | null;
  cumBudget: number;
}

interface ChartMeta {
  fy_start: string | null;
  fy_end: string | null;
  element_name: string;
}

interface TickInterval {
  days?: number;
  weeks?: number;
  months?: number;
}

function fmtDateUK(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export default function BudgetChart({
  cobDate = undefined,
  elementName = "Total RMS",
  apiBase = process.env.REACT_APP_API_BASE_URL,
}: BudgetChartProps) {
  const [data, setData] = useState<ChartRow[]>([]);
  const [meta, setMeta] = useState<ChartMeta>({
    fy_start: null,
    fy_end: null,
    element_name: elementName,
  });
  const tickInterval = useMemo(() => chooseTickInterval(data), [data]);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string>("");

  const url = useMemo(() => {
      if (!cobDate) return undefined;
      const formattedSelectedDate = cobDate.toISOString().split('T')[0];
    const qs = new URLSearchParams({
      cob_date: formattedSelectedDate,
      element_name: elementName,
    });
    return `${apiBase}/pnl_budget_rms_plot?${qs.toString()}`;
  }, [apiBase, cobDate, elementName]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!url) return;
      setLoading(true);
      setErr("");
      try {
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();

        const rows: any[] = Array.isArray(j.rows) ? j.rows : [];
        const mapped = rows
          .map((r: any) => {
            const dt = new Date(r.cob_date);
            if (Number.isNaN(dt.getTime())) return null;
            return {
              date: dt,
              dateText: fmtDateUK(dt),
               pnl_fytd: (r.pnl_fytd === null || r.pnl_fytd === undefined) ? null : Number(r.pnl_fytd),
              cumBudget: Number(r.cumBudget ?? 0),
            };
          })
          .filter((x): x is ChartRow => x !== null)
          .sort((a, b) => a.date.getTime() - b.date.getTime());

        if (!cancelled) {
          setData(mapped);
          setMeta({
            fy_start: j.fy_start ?? null,
            fy_end: j.fy_end ?? null,
            element_name: j.element_name ?? elementName,
          });
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [url, elementName]);

  const title = useMemo(() => {
    // const fy = meta.fy_start && meta.fy_end ? ` (${meta.fy_start} → ${meta.fy_end})` : "";
    return `${meta.element_name}`;
  }, [meta]);
function chooseTickInterval(data: ChartRow[]): TickInterval {
  if (!data || data.length < 10) return { days: 1 };

  const minT = data[0].date.getTime();
  const maxT = data[data.length - 1].date.getTime();
  const days = Math.max(1, Math.round((maxT - minT) / 86400000));

  if (days <= 14) return { days: 1 };     // up to 2 weeks -> daily
  if (days <= 120) return { weeks: 1 };   // up to ~4 months -> weekly
  return { months: 1 };                   // longer -> monthly
}
  return (
    <div style={{ width: "100%", maxWidth: 900 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div style={{ fontWeight: 400 }}>{title}</div>
        {loading ? <div style={{ fontSize: 12, opacity: 0.7 }}>Loading…</div> : null}
        {err ? <div style={{ fontSize: 12, color: "crimson" }}>{err}</div> : null}
      </div>

      <Chart
        dataSource={data}
        size={{ height: 260 }}
      >
        <Series
          type="line"
          argumentField="date"
          valueField="cumBudget" color={"#B8860B"}
          name="Budget"
            point={{ visible: false }}
        />

        <Series
          type="line"
          argumentField="date"
          valueField="pnl_fytd"
          name="PnL FYTD"
          ignoreEmptyPoints={true}
            point={{ visible: false }}
        />

              <ArgumentAxis argumentType="datetime" tickInterval={tickInterval}>
        <Label
          rotationAngle={-45}
          overlappingBehavior="rotate"
          customizeText={(e: any) => {
            const dt = new Date(e.value);
            const dd = String(dt.getDate()).padStart(2, "0");
            const mm = String(dt.getMonth() + 1).padStart(2, "0");
            const yyyy = dt.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
          }}
        />
          <Grid visible />
        </ArgumentAxis>

        <ValueAxis>
          <Grid visible />
        </ValueAxis>

        <Legend visible verticalAlignment="bottom" horizontalAlignment="center" />

        <Tooltip
          enabled
          customizeTooltip={(p: any) => {
            const { dateText, cumBudget, pnl_fytd } = p.point.data;
            return {
              html: `
                <div><b>${dateText}</b></div>
                <div>Budget: ${Number(cumBudget).toLocaleString()}</div>
                <div>PnL FYTD: ${Number(pnl_fytd).toLocaleString()}</div>
              `,
            };
          }}
        />
      </Chart>
    </div>
  );
}
